import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAppStore from '../../store/useAppStore';
import { useAppContext } from '../../hooks/useAppContext';
import { User as UserIcon, Mail, Phone, MapPin, Calendar, Briefcase, Building2, Shield, ArrowRight, BarChart2, Search, Eye, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { arSA } from 'date-fns/locale';
import { ReportStatus, ReportType } from '../../types';
import toast from 'react-hot-toast';

const statusVariant: { [key in ReportStatus]: string } = {
    [ReportStatus.Approved]: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    [ReportStatus.Pending]: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    [ReportStatus.Rejected]: 'text-rose-400 bg-rose-400/10 border-rose-400/20',
};

const typeColors: { [key in ReportType]: string } = {
    [ReportType.Sales]: 'border-l-4 border-l-blue-500',
    [ReportType.Maintenance]: 'border-l-4 border-l-orange-500',
    [ReportType.Project]: 'border-l-4 border-l-purple-500',
    [ReportType.Inquiry]: 'border-l-4 border-l-slate-500',
};

const ModernAdminEmployeeDetailScreen: React.FC = () => {
    const { employeeId: employeeIdFromParams } = useParams<{ employeeId: string }>();
    const navigate = useNavigate();
    const { users, reports } = useAppStore();
    const { updateUser } = useAppContext();

    const employee = useMemo(() => users.find(u => u.id === employeeIdFromParams), [users, employeeIdFromParams]);

    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');

    const [allowedTypes, setAllowedTypes] = useState<ReportType[]>(() => [
        ReportType.Sales,
        ReportType.Maintenance,
        ReportType.Project,
    ]);

    React.useEffect(() => {
        if (employee) {
            setAllowedTypes(employee.allowedReportTypes && employee.allowedReportTypes.length > 0
                ? employee.allowedReportTypes
                : [ReportType.Sales, ReportType.Maintenance, ReportType.Project]);
        }
    }, [employee]);

    const toggleType = (type: ReportType) => {
        setAllowedTypes(prev => {
            if (prev.includes(type)) {
                return prev.filter(t => t !== type);
            }
            return [...prev, type];
        });
    };

    const handleSavePermissions = async () => {
        if (!employee) return;
        try {
            await updateUser({ ...employee, allowedReportTypes: allowedTypes });
            toast.success('تم حفظ صلاحيات أنواع التقارير لهذا الموظف بنجاح.');
        } catch (error: any) {
            toast.error(error?.message || 'فشل حفظ صلاحيات الموظف.');
        }
    };

    const filteredReports = useMemo(() => {
        if (!employee) return [];
        return reports.filter(report => {
            if (report.employeeId !== employee.employeeId) return false;

            const searchLower = searchTerm.toLowerCase();
            const searchMatch = searchLower === '' ||
                report.id.toLowerCase().includes(searchLower) ||
                report.type.toLowerCase().includes(searchLower);

            const typeMatch = typeFilter === 'all' || report.type === typeFilter;
            const statusMatch = statusFilter === 'all' || report.status === statusFilter;
            const dateMatch = dateFilter === '' || format(new Date(report.date), 'yyyy-MM-dd') === dateFilter;

            return searchMatch && typeMatch && statusMatch && dateMatch;
        });
    }, [reports, employee, searchTerm, typeFilter, statusFilter, dateFilter]);


    if (!employee) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <div className="glass-panel p-8 rounded-2xl border border-white/10">
                    <p className="text-xl text-slate-300 mb-6">لم يتم العثور على الموظف.</p>
                    <button onClick={() => navigate('/employees')} className="modern-btn bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-lg backdrop-blur-md transition-all">
                        العودة لقائمة الموظفين
                    </button>
                </div>
            </div>
        );
    }

    const formattedJoinDate = format(new Date(employee.joinDate), 'PPP', { locale: arSA });

    return (
        <div className="space-y-8 animate-contentFadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text">ملف الموظف</h1>
                    <p className="text-slate-400 mt-1">إدارة بيانات وصلاحيات {employee.name}</p>
                </div>
                <button
                    onClick={() => navigate('/employees')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 transition-all hover:text-white"
                >
                    <ArrowRight size={18} />
                    <span>رجوع للقائمة</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Profile Info */}
                <div className="lg:col-span-1 space-y-8">
                    {/* Profile Card */}
                    <div className="glass-card p-6 relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"></div>
                        <div className="flex flex-col items-center text-center relative z-10">
                            <div className="relative mb-6 group-hover:scale-105 transition-transform duration-300">
                                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity"></div>
                                <div className="w-32 h-32 rounded-full border-2 border-white/20 bg-slate-900/80 flex items-center justify-center relative z-10 backdrop-blur-sm">
                                    <UserIcon size={64} className="text-cyan-400" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">{employee.name}</h2>
                            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10 text-cyan-300 text-sm backdrop-blur-md">
                                {employee.position}
                            </span>
                        </div>

                        <div className="mt-8 space-y-4">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-2 rounded-full bg-blue-500/20 text-blue-400">
                                    <Mail size={16} />
                                </div>
                                <span className="text-slate-300 text-sm font-mono" dir="ltr">{employee.email}</span>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-2 rounded-full bg-green-500/20 text-green-400">
                                    <Phone size={16} />
                                </div>
                                <span className="text-slate-300 text-sm font-mono" dir="ltr">{employee.phone}</span>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-2 rounded-full bg-purple-500/20 text-purple-400">
                                    <MapPin size={16} />
                                </div>
                                <span className="text-slate-300 text-sm">{employee.branch}</span>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                                <div className="p-2 rounded-full bg-amber-500/20 text-amber-400">
                                    <Calendar size={16} />
                                </div>
                                <span className="text-slate-300 text-sm">انضم في: {formattedJoinDate}</span>
                            </div>
                        </div>
                    </div>

                    {/* Work Info */}
                    <div className="glass-card p-6">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <Briefcase size={20} className="text-cyan-400" />
                            معلومات العمل
                        </h3>
                        <div className="grid grid-cols-1 gap-4">
                            <div className="flex items-center justify-between p-3 border-b border-white/5">
                                <span className="text-slate-400 text-sm">القسم</span>
                                <span className="text-white font-medium">{employee.department}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 border-b border-white/5">
                                <span className="text-slate-400 text-sm">الفرع</span>
                                <span className="text-white font-medium">{employee.branch}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 border-b border-white/5">
                                <span className="text-slate-400 text-sm">رقم الموظف</span>
                                <span className="text-white font-mono">{employee.employeeId}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Permissions & Reports */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Permissions */}
                    <div className="glass-card p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                <Shield size={20} className="text-purple-400" />
                                صلاحيات أنواع التقارير
                            </h3>
                        </div>

                        <div className="flex flex-wrap gap-3 mb-6">
                            {[ReportType.Sales, ReportType.Maintenance, ReportType.Project].map(rt => {
                                const isSelected = allowedTypes.includes(rt);
                                return (
                                    <label
                                        key={rt}
                                        className={`
                                            cursor-pointer relative overflow-hidden group px-4 py-3 rounded-xl border transition-all duration-300
                                            ${isSelected
                                                ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                                                : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:border-white/20'
                                            }
                                        `}
                                    >
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={isSelected}
                                            onChange={() => toggleType(rt)}
                                        />
                                        <div className="flex items-center gap-2">
                                            {isSelected ? <CheckCircle size={18} /> : <div className="w-[18px] h-[18px] rounded-full border border-slate-500" />}
                                            <span className="font-medium">{rt}</span>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleSavePermissions}
                                className="modern-btn bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-6 py-2 rounded-lg shadow-lg shadow-cyan-900/20"
                            >
                                حفظ الصلاحيات
                            </button>
                            <button
                                onClick={() => setAllowedTypes([ReportType.Sales, ReportType.Maintenance, ReportType.Project])}
                                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 transition-all"
                            >
                                السماح للجميع
                            </button>
                        </div>
                    </div>

                    {/* Reports Log */}
                    <div className="glass-card p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                <BarChart2 size={20} className="text-green-400" />
                                سجل التقارير <span className="text-sm font-normal text-slate-400 bg-white/5 px-2 py-0.5 rounded-full border border-white/5">{filteredReports.length}</span>
                            </h3>
                        </div>

                        {/* Filters */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-4 rounded-xl bg-black/20 border border-white/5">
                            <div className="relative">
                                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    placeholder="بحث..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pr-10 pl-3 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                                />
                            </div>
                            <select
                                value={typeFilter}
                                onChange={e => setTypeFilter(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                            >
                                <option value="all" className="bg-slate-800">كل الأنواع</option>
                                {Object.values(ReportType).map(type => <option key={type} value={type} className="bg-slate-800">{type}</option>)}
                            </select>
                            <select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                            >
                                <option value="all" className="bg-slate-800">كل الحالات</option>
                                {Object.values(ReportStatus).map(status => <option key={status} value={status} className="bg-slate-800">{status}</option>)}
                            </select>
                            <input
                                type="date"
                                value={dateFilter}
                                onChange={e => setDateFilter(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-slate-200 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all [color-scheme:dark]"
                            />
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto rounded-xl border border-white/5">
                            {filteredReports.length > 0 ? (
                                <table className="w-full text-sm text-right">
                                    <thead className="bg-white/5 text-slate-300 font-medium">
                                        <tr>
                                            <th className="px-6 py-4">النوع</th>
                                            <th className="px-6 py-4">التاريخ</th>
                                            <th className="px-6 py-4">الحالة</th>
                                            <th className="px-6 py-4">إجراء</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {filteredReports.map((report) => (
                                            <tr key={report.id} className="hover:bg-white/5 transition-colors group">
                                                <td className="px-6 py-4">
                                                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${report.type === ReportType.Sales ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                                        report.type === ReportType.Maintenance ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                                                            report.type === ReportType.Project ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                                                                'bg-slate-500/10 text-slate-400 border-slate-500/20'
                                                        }`}>
                                                        {report.type}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-300">
                                                    <div className="flex items-center gap-2">
                                                        <Clock size={14} className="text-slate-500" />
                                                        {new Date(report.date).toLocaleDateString('ar-SA')}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusVariant[report.status]}`}>
                                                        {report.status === ReportStatus.Approved && <CheckCircle size={12} />}
                                                        {report.status === ReportStatus.Rejected && <XCircle size={12} />}
                                                        {report.status === ReportStatus.Pending && <Clock size={12} />}
                                                        {report.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => navigate(`/reports/${report.id}`)}
                                                        className="p-2 rounded-lg bg-white/5 hover:bg-cyan-500/20 text-slate-400 hover:text-cyan-400 transition-all opacity-0 group-hover:opacity-100"
                                                        title="عرض التفاصيل"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                                        <Search size={32} className="text-slate-500" />
                                    </div>
                                    <p className="text-slate-400">لا توجد تقارير مطابقة للفلاتر.</p>
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setTypeFilter('all');
                                            setStatusFilter('all');
                                            setDateFilter('');
                                        }}
                                        className="mt-4 text-cyan-400 hover:text-cyan-300 text-sm"
                                    >
                                        مسح الفلاتر
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModernAdminEmployeeDetailScreen;
